/**
 * Installs a `toJSON` shim on `Element.prototype` so that browser extensions
 * that call `JSON.stringify` on DOM nodes being inserted (e.g. antivirus /
 * shopping extensions that patch `appendChild`) don't trip on the circular
 * reference React creates via `__reactFiber$*` → FiberNode → `stateNode` →
 * same element.
 *
 * `toJSON` is consulted by `JSON.stringify` before the object graph is walked,
 * so it prevents the cycle from ever being reached. It is NOT consulted by
 * `structuredClone`, `postMessage`, or Sentry/rrweb's own DOM serializers,
 * so those code paths are unaffected.
 *
 * We target `Element.prototype` rather than `Node.prototype` to keep the
 * blast radius narrow: the observed crash root is `HTMLAnchorElement`, and
 * all HTML/SVG elements inherit from `Element`.
 */
export function installDomSerializationGuard(): void {
  // Don't clobber an existing definition (e.g. from another library).
  if ('toJSON' in Element.prototype) {
    return;
  }

  Object.defineProperty(Element.prototype, 'toJSON', {
    value(this: Element) {
      // SVG elements expose className as SVGAnimatedString, not a plain string.
      const className =
        typeof this.className === 'string' ? this.className : undefined;

      return { nodeName: this.nodeName, id: this.id, className };
    },
    enumerable: false,
    configurable: true,
    writable: true
  });
}

/**
 * Returns true when `error` is the specific TypeError thrown by
 * `JSON.stringify` when it encounters a React fiber cycle on a DOM node.
 *
 * We require that the message mentions a React-internal property key
 * (`__reactFiber` or `__reactProps`) so that a genuine circular-JSON bug in
 * our own code is not silently swallowed by Sentry.
 */
export function isCircularDomSerializationError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const msg = error.message;

  return (
    msg.includes('Converting circular structure to JSON') &&
    (msg.includes('__reactFiber') || msg.includes('__reactProps'))
  );
}
